import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { I } from '../icons-ds';
import {
  Button, FilterChip, Segmented, SectionHeader, SearchBar, Empty, Spinner,
} from '../ui';
import { MobileNav, useViewport } from '../layout';
import { PhraseCard, PhraseCardLayout } from './PhraseCard';
import { usePhrases } from '../lib/PhrasesContext';
import { useTheme } from '../lib/ThemeContext';
import type { Phrase, PrimaryTag } from '../lib/types';
import type { FilterChipKind } from '../ui';
import { hydrateSearchResults, type SearchHit } from '../lib/search';
import { usePhraseSearch } from '../hooks/usePhraseSearch';
import './search.css';

type FilterDef = { id: string; kind: FilterChipKind; icon: React.ReactNode };

const FILTERS: FilterDef[] = [
  { id: 'Inpatient', kind: 'inpatient', icon: <I.check size={14} stroke={2.4} /> },
  { id: 'Outpatient', kind: 'outpatient', icon: <I.check size={14} stroke={2.4} /> },
  { id: 'ICU', kind: 'icu', icon: <I.bolt size={14} /> },
  { id: 'ED', kind: 'ed', icon: <I.bolt size={14} /> },
  { id: 'Examination', kind: 'exam', icon: <I.check size={14} stroke={2.4} /> },
  { id: 'Protocols', kind: 'protocols', icon: <I.list size={14} /> },
  { id: 'References', kind: 'references', icon: <I.bookmark size={14} /> },
];

const RESULTS_PAGE_SIZE = 60;

export function HomeScreen() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const isMobile = viewport === 'mobile';

  const { phrases, folders, search, recentIds, clearRecents, toggleFavorite } = usePhrases();
  const { layout, setLayout, defaultFolder } = useTheme();

  const query = searchParams.get('q') || '';
  const { results: rankedResults, searching } = usePhraseSearch(query, search);
  const [resultLimit, setResultLimit] = useState(RESULTS_PAGE_SIZE);
  const gridView = !isMobile && layout === 'grid';
  const activeFilters = useMemo(() => {
    const raw = searchParams.get('tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  // Folder context (when rendered at /folder/:parent[/:child]).
  const parentFolder = params.parent
    ? folders.find((f) => f.id === params.parent)
    : undefined;
  const childFolder = parentFolder?.children?.find((c) => c.id === params.child);
  const folderKind: PrimaryTag | undefined = parentFolder?.kind;

  const setQuery = (q: string) => {
    const next = new URLSearchParams(searchParams);
    if (q) next.set('q', q);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const setActiveFilters = (filters: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (filters.length) next.set('tags', filters.join(','));
    else next.delete('tags');
    setSearchParams(next, { replace: true });
  };

  const open = (p: Phrase) => {
    const suffix = query.trim() ? '?q=' + encodeURIComponent(query.trim()) : '';
    navigate('/phrase/' + p.id + suffix);
  };

  // Index is built once in context; resolve hits back to phrases (with live
  // favorite state) by id.
  const byId = useMemo(() => new Map(phrases.map((p) => [p.id, p])), [phrases]);

  const inFolder = Boolean(parentFolder);

  // On a fresh arrival at Search — not inside a folder, with no filter or query
  // already in the URL — scope to the user's default folder if one is set. Runs
  // once per mount, so the user can still clear the chip to broaden their search.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (inFolder || defaultFolder === 'all') return;
    if (searchParams.get('tags') || searchParams.get('q')) return;
    const id = FILTERS.find((f) => f.kind === defaultFolder)?.id;
    if (id) setActiveFilters([id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const childName = childFolder?.name;
  const folderScoped = useMemo(() => {
    let list = folderKind ? phrases.filter((p) => p.primaryTag === folderKind) : phrases;
    // When a subfolder is open (e.g. Inpatient → Cardiology, Examination → Proc),
    // narrow to phrases carrying that subfolder's tag so the click actually filters.
    if (childName) list = list.filter((p) => p.tags.includes(childName));
    return list;
  }, [phrases, folderKind, childName]);

  // Results respect the folder scope + filter chips, then either rank by the
  // query (when present) or fall back to source order.
  const matches = useMemo<SearchHit[]>(() => {
    const allowed = new Set(
      folderScoped
        .filter((p) => !activeFilters.length || p.tags.some((t) => activeFilters.includes(t)))
        .map((p) => p.id)
    );
    if (!query.trim()) {
      return folderScoped
        .filter((p) => allowed.has(p.id))
        .map((p) => ({ phrase: p, score: 0, terms: [] }));
    }
    return hydrateSearchResults(rankedResults, byId).filter((h) => allowed.has(h.phrase.id));
  }, [rankedResults, byId, folderScoped, activeFilters, query]);

  useEffect(() => setResultLimit(RESULTS_PAGE_SIZE), [query, activeFilters, folderKind, childName]);
  const visibleMatches = matches.slice(0, resultLimit);

  const favorites = phrases.filter((p) => p.favorite);
  const recentPhrases = useMemo(
    () => recentIds.map((id) => byId.get(id)).filter((p): p is Phrase => Boolean(p)).slice(0, 8),
    [recentIds, byId]
  );

  const hasQueryOrFilter = Boolean(query || activeFilters.length);
  const title = inFolder ? (childFolder?.name || parentFolder?.name || 'Folder') : 'Search';
  const subtitle = inFolder
    ? `${folderScoped.length} phrases in ${childFolder?.name || parentFolder?.name}`
    : `${phrases.length} phrases across ${folders.length} folders`;

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-canvas)' }}>
      <div
        style={{
          padding: isMobile ? '0 16px 24px' : '0 32px 32px',
          maxWidth: 980, margin: '0 auto',
        }}
      >
        <header className="dp-search-header">
          {isMobile ? (
            <MobileNav
              title={title}
              onBack={inFolder ? () => navigate('/folders') : undefined}
              backTitle={inFolder ? 'Folders' : undefined}
              sticky={false}
              transparent
            />
          ) : (
            <div className="dp-search-intro" style={{ marginBottom: 24 }}>
              <div className="t-largetitle" style={{ marginBottom: 4 }}>{title}</div>
              <div className="t-subhead" style={{ color: 'var(--fg-secondary)' }}>{subtitle}</div>
            </div>
          )}

          <SearchBar ariaLabel="Search phrases" value={query} onChange={setQuery} autoFocus={!isMobile} showShortcut={!isMobile} size={isMobile ? 'md' : 'lg'} />

        <div
          className="dp-chip-row"
          style={{
            display: 'flex', gap: 8, overflowX: 'auto',
            scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}
        >
          {FILTERS.map((f) => (
            <FilterChip
              key={f.id}
              kind={f.kind}
              icon={activeFilters.includes(f.id) ? f.icon : undefined}
              active={activeFilters.includes(f.id)}
              onClick={() =>
                setActiveFilters(
                  activeFilters.includes(f.id)
                    ? activeFilters.filter((x) => x !== f.id)
                    : [...activeFilters, f.id]
                )
              }
            >
              {f.id}
            </FilterChip>
          ))}
        </div>

        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 0,
          }}
        >
          <div role="status" className="t-footnote dp-search-count" style={{ color: 'var(--fg-secondary)' }}>
            {hasQueryOrFilter || inFolder ? (
              <>
                {searching ? 'Searching…' : `${matches.length} ${matches.length === 1 ? 'result' : 'results'}`}
                {query && (
                  <>
                    {' '}for{' '}
                    <span style={{ color: 'var(--fg-primary)', fontWeight: 600 }}>"{query}"</span>
                  </>
                )}
              </>
            ) : (
              <>All phrases</>
            )}
          </div>
          {!isMobile && (
            <Segmented<PhraseCardLayout>
              value={layout}
              onChange={setLayout}
              options={[
                { value: 'list', icon: <I.list size={14} />, label: 'List' },
                { value: 'grid', icon: <I.grid size={14} />, label: 'Grid' },
              ]}
            />
          )}
        </div>
        </header>

        {!hasQueryOrFilter && !inFolder ? (
          <>
            {recentPhrases.length > 0 && (
              <>
                <SectionHeader
                  action={
                    <button
                      onClick={clearRecents}
                      style={{
                        background: 'transparent', border: 0, cursor: 'pointer',
                        padding: '2px 4px', color: 'var(--accent)', fontSize: 13, fontWeight: 600,
                      }}
                    >
                      Clear
                    </button>
                  }
                >
                  Recently used
                </SectionHeader>
                <div
                  style={{
                    ...(gridView
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }
                      : { display: 'flex', flexDirection: 'column', gap: 8 }),
                    marginBottom: 28,
                  }}
                >
                  {recentPhrases.map((p) => (
                    <PhraseCard
                      key={p.id}
                      phrase={p}
                      onOpen={() => open(p)}
                      onFavorite={() => toggleFavorite(p.id)}
                      layout={gridView ? 'grid' : 'list'}
                    />
                  ))}
                </div>
              </>
            )}
            {favorites.length > 0 && (
              <>
                <SectionHeader>Favorites</SectionHeader>
                <div
                  style={{
                    ...(gridView
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }
                      : { display: 'flex', flexDirection: 'column', gap: 8 }),
                    marginBottom: 28,
                  }}
                >
                  {favorites.map((p) => (
                    <PhraseCard
                      key={p.id}
                      phrase={p}
                      onOpen={() => open(p)}
                      onFavorite={() => toggleFavorite(p.id)}
                      layout={gridView ? 'grid' : 'list'}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : searching && query.trim() ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <Spinner size={24} />
          </div>
        ) : matches.length === 0 ? (
          <Empty
            icon={<I.search size={28} />}
            title="No matches"
            body="Try a different keyword, or remove a filter to broaden your search."
            action={
              <Button
                variant="tinted"
                onClick={() => {
                  setQuery('');
                  setActiveFilters([]);
                }}
              >
                Clear filters
              </Button>
            }
          />
        ) : gridView ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 12,
            }}
          >
            {visibleMatches.map((h) => (
              <PhraseCard
                key={h.phrase.id}
                phrase={h.phrase}
                snippet={h.snippet}
                terms={h.terms}
                onOpen={() => open(h.phrase)}
                onFavorite={() => toggleFavorite(h.phrase.id)}
                layout="grid"
              />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleMatches.map((h) => (
              <PhraseCard
                key={h.phrase.id}
                phrase={h.phrase}
                snippet={h.snippet}
                terms={h.terms}
                onOpen={() => open(h.phrase)}
                onFavorite={() => toggleFavorite(h.phrase.id)}
                layout="list"
              />
            ))}
          </div>
        )}
        {hasQueryOrFilter && visibleMatches.length < matches.length && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 4px' }}>
            <Button variant="tinted" onClick={() => setResultLimit((limit) => limit + RESULTS_PAGE_SIZE)}>
              Show more ({matches.length - visibleMatches.length} remaining)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomeScreen;
